/**
 * create-board/useCreateBoardForm — all form state, reset/esc/template-load
 * effects, quick-start application, and submit handling for CreateBoardDialog.
 * Extracted VERBATIM from the former monolith so the orchestrator keeps only
 * the JSX render. No behaviour change.
 */

import { useEffect, useRef, useState } from 'react'
import type { Board, BoardKind, BoardVisibility } from '../../types'
import type { InvitedMember } from '../BoardInvitePicker'
import { slugifyBoardName } from '../../utils/boardSlug'
import { CONSTRUCTION_QUICK_STARTS, type CreateBoardDialogProps } from './model'

type QuickStart = (typeof CONSTRUCTION_QUICK_STARTS)[number]

export function useCreateBoardForm({
  open,
  onClose,
  organisationId,
  onCreate,
  kindChoices,
  loadTemplateCandidates,
}: Pick<
  CreateBoardDialogProps,
  'open' | 'onClose' | 'organisationId' | 'onCreate' | 'loadTemplateCandidates'
> & { kindChoices: NonNullable<CreateBoardDialogProps['kindChoices']> }) {
  const [name, setName] = useState('')
  const [kind, setKind] = useState<BoardKind>(kindChoices[0]?.value ?? 'CUSTOM')
  // FEAT-2026-07-03-KANBAN-DEFAULT-PRIVATE — a new board defaults to
  // "Private" (just the creator), NOT org-wide or members. The creator can
  // still opt into 'BOARD_MEMBERS' (and invite people) or 'ORG' in the
  // dialog. Operator ask: every hand-made board should start private and be
  // shared deliberately, not leak to the org/members by default.
  const [visibility, setVisibility] = useState<BoardVisibility>('PRIVATE')
  const [description, setDescription] = useState('')
  // Pending invites for a "Members only" board (granted by the parent on
  // create). Lives here so switching visibility away and back keeps the draft.
  const [invited, setInvited] = useState<InvitedMember[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Template-clone state. `cloneFromBoardId` empty = use the
  // canonical kind picker above; populated = use the template
  // (and the canonical kind picker's value still drives `kind`,
  // which the parent's `onCreate` handler can override to CUSTOM
  // if it wants).
  const [cloneFromBoardId, setCloneFromBoardId] = useState<string>('')
  const [cloneConfig, setCloneConfig] = useState<boolean>(true)
  const [templates, setTemplates] = useState<Board[] | null>(null)
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false)
  const [templateFilter, setTemplateFilter] = useState('')
  // FEAT-2026-06-07-KANBAN-CONSTRUCTION — which construction quick-start
  // (if any) the operator tapped, so we can highlight it.
  const [quickStart, setQuickStart] = useState<string | null>(null)

  const applyQuickStart = (qs: QuickStart) => {
    setQuickStart(qs.id)
    setName(qs.name)
    setKind(qs.kind)
    setDescription(qs.description)
    // A construction scaffold and a clone-from-board are mutually
    // exclusive intents; clear any clone selection.
    setCloneFromBoardId('')
  }

  useEffect(() => {
    if (open) {
      // Reset on open so a previously-cancelled draft doesn't leak.
      setName('')
      setKind(kindChoices[0]?.value ?? 'CUSTOM')
      // Default private on every fresh open — see the useState above.
      setVisibility('PRIVATE')
      setDescription('')
      setInvited([])
      setError(null)
      setCloneFromBoardId('')
      setCloneConfig(true)
      setTemplatePickerOpen(false)
      setTemplateFilter('')
      setQuickStart(null)
      // Focus the title field (let the modal mount first).
      const t = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [open, kindChoices])

  // Lazy-load template candidates when the picker opens. Subsequent
  // opens reuse the cached list so the picker stays snappy.
  useEffect(() => {
    if (!templatePickerOpen) return
    if (templates !== null) return
    if (!loadTemplateCandidates) return
    setTemplatesLoading(true)
    loadTemplateCandidates()
      .then((list) => setTemplates(list))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoading(false))
  }, [templatePickerOpen, templates, loadTemplateCandidates])

  const selectedTemplate = (templates ?? []).find((b) => b.id === cloneFromBoardId)

  // Esc closes — universal modal contract.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    setError(null)
    try {
      await onCreate({
        name: trimmed,
        kind,
        visibility,
        description: description.trim() || undefined,
        organisation: organisationId,
        cloneFromBoardId: cloneFromBoardId || undefined,
        cloneConfig: cloneFromBoardId ? cloneConfig : undefined,
        // Only forward invites for a Members-only board.
        invitedMembers:
          visibility === 'BOARD_MEMBERS' && invited.length ? invited : undefined,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create board')
    } finally {
      setBusy(false)
    }
  }

  const slug = slugifyBoardName(name)

  return {
    name, setName,
    kind, setKind,
    visibility, setVisibility,
    description, setDescription,
    invited, setInvited,
    busy,
    error,
    inputRef,
    cloneFromBoardId, setCloneFromBoardId,
    cloneConfig, setCloneConfig,
    templates,
    templatesLoading,
    templatePickerOpen, setTemplatePickerOpen,
    templateFilter, setTemplateFilter,
    quickStart,
    applyQuickStart,
    selectedTemplate,
    handleSubmit,
    slug,
  }
}
